import Image from 'next/image'
import Link from 'next/link'
import { PortableText } from '@portabletext/react'
import { urlFor } from '@/sanity/lib/image'
import SanityTable from '@/components/SanityTable'
import { decorateTripUrl, isTripUrl, shouldSkipUrl } from '@/lib/affiliates/utils/trip'
import { decorateBookingUrl, isBookingUrl } from '@/lib/affiliates/utils/booking'
import { decorateTripsterUrl, isTripsterUrl } from '@/lib/affiliates/utils/tripster'
import { decorateGenericAffiliateUrl } from '@/lib/affiliates/utils/decorateGeneric'
import { buildProviderLink, isExternalLink } from './providerLinkHelpers'
import { normalizeSlug } from '@/utils/categoryHelpers'
import { getImageFromMainImage } from './imageHelpers'
import { IMAGE_SIZES, IMAGE_QUALITY, UI_CONSTANTS } from './blogConstants'
import AffiliateLink from '@/components/common/AffiliateLink'
import { buildAffiliateSubId, getAffiliateMetadata } from '@/lib/affiliates/metadata'
import { wrapAffiliateLink } from '@/lib/affiliates/proxy'
import ArticlePartnerCard from '@/components/blog/ArticlePartnerCard'
import { articleBlockId } from './articleStructure'
import { resolveDeletedBlogHref } from '@/lib/deletedBlogRedirects'
import { transliterateCyrillic } from '@/lib/content/transliterate'

/** Типы marks, которые рендерят <a> или <Link> (генерируют anchor-элементы). */
const ANCHOR_MARK_TYPES = new Set([
    'providerLink', 'internalLink', 'cityLink', 'serviceLink', 'airportLink', 'poiLink', 'countryLink'
])

/**
 * Убирает конфликт вложенных <a>: если span имеет одновременно mark `link`
 * и один из ANCHOR_MARK_TYPES, удаляем `link` — более специфичный mark победит.
 * Возвращает новый массив (иммутабельно).
 */
export function deduplicateLinkMarks(body) {
    if (!Array.isArray(body)) return body
    return body.map(block => {
        if (!block?.markDefs || !block?.children) return block
        const anchorKeys = new Set()
        const linkKeys = new Set()
        for (const md of block.markDefs) {
            if (ANCHOR_MARK_TYPES.has(md._type)) anchorKeys.add(md._key)
            if (md._type === 'link') linkKeys.add(md._key)
        }
        if (anchorKeys.size === 0 || linkKeys.size === 0) return block
        const newChildren = block.children.map(child => {
            if (!child.marks || child.marks.length < 2) return child
            const hasAnchor = child.marks.some(m => anchorKeys.has(m))
            const hasLink = child.marks.some(m => linkKeys.has(m))
            if (!hasAnchor || !hasLink) return child
            return { ...child, marks: child.marks.filter(m => !anchorKeys.has(m)) }
        })
        return { ...block, children: newChildren }
    })
}

/**
 * Если в массиве blocks нет ни одного h2, но есть h3 — промоутим все h3 в h2.
 * Защита от контентной ошибки (h1 → h3 без промежуточного h2 ломает иерархию для SEO/Google).
 * Безопасно: коллизии с существующим h2 невозможны, потому что мы запускаемся только когда h2 = 0.
 * Иммутабельно: возвращает новый массив, исходный не мутирует.
 */
export function promoteH3IfNoH2(body) {
    if (!Array.isArray(body) || body.length === 0) return body;
    let hasH2 = false;
    let hasH3 = false;
    for (const b of body) {
        if (b?.style === 'h2') { hasH2 = true; break; }
        if (b?.style === 'h3') hasH3 = true;
    }
    if (hasH2 || !hasH3) return body;
    return body.map(b => (b?.style === 'h3' ? { ...b, style: 'h2' } : b));
}

export { extractFaqFromBody } from './faqExtract';

function childrenText(children) {
    if (children == null || typeof children === 'boolean') return '';
    if (typeof children === 'string' || typeof children === 'number') return String(children);
    if (Array.isArray(children)) return children.map(childrenText).join('');
    if (typeof children === 'object' && children.props) return childrenText(children.props.children);
    return '';
}

function isKeypointsHeading(text) {
    return /^(ключевые моменты|главное|коротко|важное|key takeaways|quick takeaways|highlights)$/i.test((text || '').trim());
}

/** Slug считается невалидным для URL (избегаем страниц с переадресацией в отчёте Google). */
function isInvalidSlug(s) {
    return !s || s === 'null' || s === 'undefined' || String(s).trim() === '';
}

/**
 * Получает slug страны из поста
 * @param {Object} post - Объект поста из Sanity
 * @returns {string|null} Slug страны или null
 */
function getPostCountrySlug(post) {
    if (!post) return null

    // Если статья глобальная, возвращаем null
    if (post.isGlobal) return null

    // Берем первую страну из массива
    const firstCountry = post.countries?.[0]
    return firstCountry?.slug?.current || firstCountry?.slug || null
}

/**
 * Создает конфигурацию компонентов для PortableText
 * @param {Object} params
 * @param {string} params.language - Язык ('ru' или 'en')
 * @param {Object} params.primaryCategory - Основная категория статьи
 * @param {Object} params.tripAffiliateConfig - Конфигурация для Trip.com ссылок
 * @param {Object} params.post - Объект поста (для получения информации о странах)
 * @returns {Object} Конфигурация компонентов PortableText
 */
export function createPortableTextConfig({ language, primaryCategory, tripAffiliateConfig, bookingAffiliateConfig, post, gptData = null }) {
    // GF: единый акцент «pine» вместо пер-категорийных цветов
    const categoryColor = '#1e466e'
    const postCountrySlug = getPostCountrySlug(post)
    const postSlug = post?.slug?.current || post?.slug || 'unknown-page'
    const bodySubId = buildAffiliateSubId({ language, pageSlug: postSlug, position: 'blog-body' })

    return {
        types: {
            // Защита от нестандартных корневых блоков со span
            span: ({ children }) => <span>{children}</span>,
            gygCityWidget: () => null,
            articleCallout: ({ value }) => {
                const tone = ['tip', 'warning', 'local', 'summary'].includes(value?.tone)
                    ? value.tone
                    : 'tip'
                const defaultLabel = {
                    tip: language === 'ru' ? 'Совет' : 'Tip',
                    warning: language === 'ru' ? 'Важно' : 'Important',
                    local: language === 'ru' ? 'На месте' : 'Local note',
                    summary: language === 'ru' ? 'Коротко' : 'In short',
                }[tone]

                return (
                    <aside className={`article-callout article-callout-${tone}`}>
                        <p className="article-callout-label">{value?.label || defaultLabel}</p>
                        {value?.title && <h3>{value.title}</h3>}
                        {Array.isArray(value?.body) && (
                            <div className="article-callout-body">
                                <PortableText value={value.body} />
                            </div>
                        )}
                    </aside>
                )
            },
            articlePartnerCard: ({ value }) => (
                <ArticlePartnerCard
                    value={value}
                    language={language}
                    country={postCountrySlug}
                    pageSlug={postSlug}
                />
            ),
            articleStatCards: ({ value }) => {
                const cards = Array.isArray(value?.cards)
                    ? value.cards.filter(c => c?.value)
                    : []
                if (cards.length === 0) return null
                return (
                    <div className="article-stat-cards">
                        {value?.title && <p className="article-stat-cards-title">{value.title}</p>}
                        <div className="article-stat-cards-grid" data-count={cards.length}>
                            {cards.map((card, idx) => (
                                <div className="article-stat-card" key={card._key || idx}>
                                    <div className="article-stat-card-value">{card.value}</div>
                                    {card.label && <div className="article-stat-card-label">{card.label}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                )
            },
            image: ({ value }) => {
                // Используем функцию getImageFromMainImage для правильного извлечения изображения
                // Она обрабатывает разные форматы: обычное изображение, licensedImage, photoAsset
                const imageSource = getImageFromMainImage(value);

                if (!imageSource) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ image: нет данных изображения', value);
                    }
                    return null;
                }

                // Проверяем, что у изображения есть asset (либо полный объект, либо ссылка _ref)
                if (!imageSource.asset && !imageSource._ref) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ image: нет asset в изображении', imageSource);
                    }
                    return null;
                }

                let imageUrl;
                try {
                    imageUrl = urlFor(imageSource)
                        .width(IMAGE_SIZES.BLOG_POST.width)
                        .height(IMAGE_SIZES.BLOG_POST.height)
                        .quality(IMAGE_QUALITY.MEDIUM)
                        .auto('format')
                        .url();
                } catch (error) {
                    if (process.env.NODE_ENV === 'development') {
                        console.error('❌ image: ошибка при генерации URL', error, { value, imageSource });
                    }
                    return null;
                }

                if (!imageUrl) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ image: не удалось сгенерировать URL', { value, imageSource });
                    }
                    return null;
                }

                return (
                    <figure className="article-figure my-8 sm:my-10">
                        <div className="relative overflow-hidden rounded-sm w-full">
                            <Image
                                src={imageUrl}
                                alt={value.alt || UI_CONSTANTS.DEFAULT_IMAGE_ALT}
                                width={IMAGE_SIZES.BLOG_POST.width / 2}
                                height={IMAGE_SIZES.BLOG_POST.height / 2}
                                className="w-full h-auto object-cover"
                                quality={IMAGE_QUALITY.MEDIUM}
                                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 800px"
                            />
                            {value.caption && (
                                <figcaption
                                    className="article-figcaption-overlay"
                                    style={{
                                        position: 'absolute',
                                        bottom: '8px',
                                        right: '8px',
                                        padding: '4px 8px',
                                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                        color: 'rgba(255, 255, 255, 0.75)',
                                        fontSize: '0.75rem',
                                        borderRadius: '3px',
                                        backdropFilter: 'blur(2px)',
                                        fontFamily: 'var(--font-source-serif)',
                                        lineHeight: '1.3',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}
                                >
                                    {value.caption}
                                </figcaption>
                            )}
                        </div>
                    </figure>
                );
            },
            gygWidget: () => null,
            viatorWidget: () => null,
            simpleTable: ({ value }) => (
                <SanityTable
                    table={value}
                    lang={language}
                    categoryColor={categoryColor}
                    postSlug={post?.slug?.current ?? ''}
                    gptData={gptData}
                />
            ),
            // Блок фото из библиотеки
            photoAssetReference: ({ value }) => {
                if (!value?.photoAsset) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ photoAssetReference: нет photoAsset', value);
                    }
                    return null;
                }

                const photoAsset = value.photoAsset;
                const image = photoAsset.image;

                if (!image) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ photoAssetReference: нет image в photoAsset', photoAsset);
                    }
                    return null;
                }

                const alt = photoAsset.alt || photoAsset.sourceTitle || UI_CONSTANTS.DEFAULT_IMAGE_ALT;
                const sourceTitle = photoAsset.sourceTitle;
                const sourceAuthor = photoAsset.sourceAuthor;

                // Формируем подпись: автор, если есть, иначе название
                let caption = null;
                if (sourceAuthor) {
                    caption = language === 'en' ? transliterateCyrillic(sourceAuthor) : sourceAuthor;
                } else if (sourceTitle) {
                    caption = sourceTitle;
                }

                const imageUrl = urlFor(image)
                    .width(IMAGE_SIZES.BLOG_POST.width)
                    .height(IMAGE_SIZES.BLOG_POST.height)
                    .quality(IMAGE_QUALITY.MEDIUM)
                    .auto('format')
                    .url();

                return (
                    <figure className="article-figure my-8 sm:my-10">
                        <div className="relative overflow-hidden rounded-sm w-full">
                            <Image
                                src={imageUrl}
                                alt={alt}
                                width={IMAGE_SIZES.BLOG_POST.width / 2}
                                height={IMAGE_SIZES.BLOG_POST.height / 2}
                                className="w-full h-auto object-cover"
                                quality={IMAGE_QUALITY.MEDIUM}
                                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 800px"
                            />
                            {caption && (
                                <figcaption
                                    className="article-figcaption-overlay"
                                    style={{
                                        position: 'absolute',
                                        bottom: '8px',
                                        right: '8px',
                                        padding: '4px 8px',
                                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                        color: 'rgba(255, 255, 255, 0.75)',
                                        fontSize: '0.75rem',
                                        borderRadius: '3px',
                                        backdropFilter: 'blur(2px)',
                                        fontFamily: 'var(--font-source-serif)',
                                        lineHeight: '1.3',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}
                                >
                                    {caption}
                                </figcaption>
                            )}
                        </div>
                    </figure>
                );
            },
            // Блок ссылки на статью
            postLink: ({ value }) => {
                if (!value?.post?.slug?.current) return null;

                const postSlug = value.post.slug.current;
                const linkText = value.text || (language === 'ru' ? value.post.titleRu : value.post.titleEn) || 'Статья';
                const href = resolveDeletedBlogHref(`/${language}/blog/${postSlug}`);

                return (
                    <Link
                        href={href}
                        className="article-link inline-flex items-center"
                        style={{ textDecorationColor: categoryColor }}
                    >
                        {linkText}
                    </Link>
                );
            },
            // Блок ссылки на провайдера
            providerLink: ({ value }) => {
                if (!value?.provider?.code) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('❌ ProviderLink: no provider code', value);
                    }
                    return null;
                }

                const provider = value.provider;
                if (provider.active === false) return null;
                const linkText = value.text || provider.title || provider.code;
                let href = buildProviderLink(provider, language, postCountrySlug, {
                    pageSlug: postSlug,
                    position: 'blog-body-block',
                });
                if (isTripsterUrl(href)) {
                    href = decorateTripsterUrl(href, post?.slug?.current ?? '')
                }
                const isExternal = isExternalLink(href);

                return (
                    <AffiliateLink
                        href={href}
                        provider={provider.code}
                        vertical={provider.vertical}
                        country={postCountrySlug}
                        position="blog-body-block"
                        label={linkText}
                        target={isExternal ? "_blank" : undefined}
                        rel={isExternal ? "nofollow noopener noreferrer sponsored" : undefined}
                        className="article-link inline-flex items-center"
                        style={{ textDecorationColor: categoryColor }}
                    >
                        {linkText}
                    </AffiliateLink>
                );
            },
        },
        marks: {
            link: ({ children, value }) => {
                const originalHref = resolveDeletedBlogHref(value?.href || '#')

                let finalHref = originalHref

                // Пропускаем партнёрские сети (tp.media и др.) - логика в shouldSkipUrl
                if (!shouldSkipUrl(originalHref)) {
                    // Trip.com: добавляем партнёрские параметры и правильный домен
                    if (isTripUrl(originalHref)) {
                        finalHref = decorateTripUrl(originalHref, tripAffiliateConfig, language)
                    }
                    // Booking.com: оборачиваем в Travelpayouts redirect
                    else if (isBookingUrl(originalHref)) {
                        finalHref = decorateBookingUrl(originalHref, bookingAffiliateConfig, language)
                    }
                    // Tripster: добавляем партнёрские параметры
                    else if (isTripsterUrl(originalHref)) {
                        finalHref = decorateTripsterUrl(originalHref, post?.slug?.current ?? '')
                    }
                    // Прочие query-param-партнёры (Viator, yesim, GetYourGuide, Kiwitaxi):
                    // дописываем партнёрскую метку, если автор вставил голую ссылку.
                    else {
                        finalHref = decorateGenericAffiliateUrl(originalHref, language)
                    }
                }

                // Проверяем, является ли ссылка внешней
                const isExternal = /^https?:\/\//i.test(finalHref)
                const affiliate = getAffiliateMetadata(finalHref)

                if (affiliate) {
                    const trackedHref = wrapAffiliateLink(finalHref, {
                        provider: affiliate.provider,
                        subId: bodySubId,
                    })
                    return (
                        <AffiliateLink
                            href={trackedHref}
                            provider={affiliate.provider}
                            vertical={affiliate.vertical}
                            country={postCountrySlug}
                            position="blog-body"
                            label={childrenText(children)}
                            className="article-link"
                            rel="nofollow noopener noreferrer sponsored"
                            style={{ textDecorationColor: categoryColor }}
                        >
                            {children}
                        </AffiliateLink>
                    )
                }

                // Не партнёрская внешняя ссылка — это источник (закон, министерство, сайт музея):
                // rel="sponsored" на ней говорил поисковикам, что сайт продаёт ссылки на gov.me и
                // almeezan.qa (ревью 30.09.2026). Партнёрские ссылки выше идут через AffiliateLink.
                return (
                    <a
                        href={finalHref}
                        target={isExternal ? "_blank" : undefined}
                        rel={isExternal ? "noopener noreferrer" : undefined}
                        className="article-link"
                        style={{ textDecorationColor: categoryColor }}
                    >
                        {children}
                    </a>
                )
            },
            // Аннотация ссылки на статью внутри текста
            internalLink: ({ children, value }) => {
                if (!value?.reference?.slug?.current) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ internalLink: нет slug в reference', value)
                    }
                    return <span>{children}</span>;
                }

                const postSlug = value.reference.slug.current;
                const href = resolveDeletedBlogHref(`/${language}/blog/${postSlug}`);

                return (
                    <Link
                        href={href}
                        className="article-link"
                        style={{ textDecorationColor: categoryColor }}
                    >
                        {children}
                    </Link>
                );
            },
            // Аннотация ссылки на провайдера внутри текста
            providerLink: ({ children, value }) => {
                if (!value?.reference?.code) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('❌ providerLink: no reference code', { value, reference: value?.reference });
                    }
                    return <span>{children}</span>;
                }

                const provider = value.reference;
                if (provider.active === false) return <span>{children}</span>;
                // Если в плейсхолдере указан deepLink (формат {текст[AFF_CODE|URL]}), используем его напрямую
                let href = provider.deepLink
                    ? wrapAffiliateLink(provider.deepLink, { provider: provider.code, subId: bodySubId })
                    : buildProviderLink(provider, language, postCountrySlug, {
                        pageSlug: postSlug,
                        position: 'blog-body',
                    });

                if (isTripsterUrl(href)) {
                    href = decorateTripsterUrl(href, post?.slug?.current ?? '')
                }
                const isExternal = isExternalLink(href);

                return (
                    <AffiliateLink
                        href={href}
                        provider={provider.code}
                        vertical={provider.vertical}
                        country={postCountrySlug}
                        position="blog-body"
                        label={childrenText(children)}
                        target={isExternal ? "_blank" : undefined}
                        rel={isExternal ? "nofollow noopener noreferrer sponsored" : undefined}
                        className="article-link"
                        style={{ textDecorationColor: categoryColor }}
                    >
                        {children}
                    </AffiliateLink>
                );
            },
            // Аннотация ссылки на город внутри текста
            cityLink: ({ children, value }) => {
                if (!value?.reference?.slug?.current) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ cityLink: нет slug в reference', value)
                    }
                    return <span>{children}</span>;
                }

                const citySlug = value.reference.slug.current;
                // Пытаемся получить страну из города, иначе используем страну из поста
                const countrySlug = value.reference.countrySlug ||
                    value.reference.country?.slug?.current ||
                    postCountrySlug;

                if (isInvalidSlug(countrySlug) || isInvalidSlug(citySlug)) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ cityLink: невалидный slug страны/города (null/undefined)', { citySlug, countrySlug });
                    }
                    return <span>{children}</span>;
                }

                const href = `/${language}/${countrySlug}/${citySlug}`;

                return (
                    <Link
                        href={href}
                        className="article-link"
                        style={{ textDecorationColor: categoryColor }}
                    >
                        {children}
                    </Link>
                );
            },
            // Аннотация ссылки на сервис внутри текста
            serviceLink: ({ children, value }) => {
                if (!value?.reference?.code) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ serviceLink: нет code в reference', value)
                    }
                    return <span>{children}</span>;
                }

                const serviceCode = value.reference.code;

                if (isInvalidSlug(postCountrySlug)) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ serviceLink: не удалось определить страну для сервиса', serviceCode);
                    }
                    return <span>{children}</span>;
                }

                const href = `/${language}/${postCountrySlug}/${serviceCode}`;

                return (
                    <Link
                        href={href}
                        className="article-link"
                        style={{ textDecorationColor: categoryColor }}
                    >
                        {children}
                    </Link>
                );
            },
            // Аннотация ссылки на аэропорт внутри текста
            airportLink: ({ children, value }) => {
                const reference = value?.reference || value;
                const iata = reference?.iata;
                const countrySlug = reference?.countrySlug || postCountrySlug;

                if (!iata || isInvalidSlug(countrySlug)) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ airportLink: нет iata или невалидный countrySlug', reference);
                    }
                    return <span>{children}</span>;
                }

                const href = `/${language}/${countrySlug}/airport/${String(iata).toLowerCase()}`;

                return (
                    <Link
                        href={href}
                        className="article-link"
                        style={{ textDecorationColor: categoryColor }}
                    >
                        {children}
                    </Link>
                );
            },
            // Аннотация ссылки на достопримечательность внутри текста
            poiLink: ({ children, value }) => {
                // Проверяем разные варианты структуры данных
                // PortableText может передавать данные по-разному
                const reference = value?.reference || value;
                if (!reference) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ poiLink: нет reference', value)
                    }
                    return <span>{children}</span>;
                }

                const poiSlug = reference.slug?.current || reference.slug;
                if (!poiSlug) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ poiLink: нет slug', reference)
                    }
                    return <span>{children}</span>;
                }

                // Пытаемся получить страну и город из POI
                const countrySlug = reference.countrySlug ||
                    reference.country?.slug?.current ||
                    postCountrySlug;
                const citySlug = reference.citySlug ||
                    reference.city?.slug?.current ||
                    reference.city?.slug;

                if (isInvalidSlug(citySlug) || isInvalidSlug(countrySlug)) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ poiLink: невалидный slug города/страны для POI', poiSlug, { citySlug, countrySlug });
                    }
                    return <span>{children}</span>;
                }

                const href = `/${language}/${countrySlug}/${citySlug}/places/${poiSlug}`;

                return (
                    <Link
                        href={href}
                        className="article-link"
                        style={{ textDecorationColor: categoryColor }}
                    >
                        {children}
                    </Link>
                );
            },
            // Аннотация ссылки на страновой хаб внутри текста ({Qatar[COUNTRY_QATAR]})
            countryLink: ({ children, value }) => {
                const reference = value?.reference || value;
                const countrySlug = reference?.slug?.current || reference?.slug;

                if (isInvalidSlug(countrySlug)) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('⚠️ countryLink: невалидный slug страны', reference);
                    }
                    return <span>{children}</span>;
                }

                const href = `/${language}/${countrySlug}`;

                return (
                    <Link
                        href={href}
                        className="article-link"
                        style={{ textDecorationColor: categoryColor }}
                    >
                        {children}
                    </Link>
                );
            },
        },
        block: {
            // H1 страницы — заголовок статьи. Блок h1 в теле давал второй H1 (6 страниц, краул
            // 29.09.2026): рендерим его как h2, вид прежний.
            h1: ({ children }) => (
                <h2
                    className="article-display font-bold leading-tight"
                    style={{
                        fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
                        marginBottom: '1.25rem',
                        marginTop: '2.5rem',
                        color: '#17211c',
                        letterSpacing: '-0.02em'
                    }}
                >
                    {children}
                </h2>
            ),
            h2: ({ children, value }) => {
                const id = articleBlockId(value) || undefined
                if (isKeypointsHeading(childrenText(children))) {
                    return (
                        <h2 id={id} className="article-keypoints-title">
                            {children}
                        </h2>
                    );
                }

                return (
                    <div id={id} className="section-break article-section-heading">
                        <h2
                            className="article-display font-semibold leading-tight"
                            style={{
                                fontSize: 'clamp(1.375rem, 4vw, 1.875rem)',
                                marginBottom: '0',
                                color: '#17211c',
                                letterSpacing: '-0.015em'
                            }}
                        >
                            {children}
                        </h2>
                    </div>
                );
            },
            h3: ({ children }) => {
                if (isKeypointsHeading(childrenText(children))) {
                    return (
                        <h3 className="article-keypoints-title">
                            {children}
                        </h3>
                    );
                }

                return (
                    <div style={{ marginTop: '2rem', marginBottom: '0.875rem' }}>
                    {/* Subtle left accent for h3 */}
                        <h3
                            className="article-display font-semibold leading-tight"
                            style={{
                                fontSize: 'clamp(1.125rem, 3vw, 1.375rem)',
                                color: '#17211c',
                                paddingLeft: '1rem',
                                borderLeft: `3px solid ${categoryColor}`,
                                marginLeft: '-0.25rem'
                            }}
                        >
                            {children}
                        </h3>
                    </div>
                );
            },
            h4: ({ children }) => (
                <h4
                    className="article-display font-medium leading-tight"
                    style={{
                        fontSize: 'clamp(1.0625rem, 2.5vw, 1.1875rem)',
                        marginBottom: '0.625rem',
                        marginTop: '1.5rem',
                        color: '#17211c',
                        fontStyle: 'italic'
                    }}
                >
                    {children}
                </h4>
            ),
            normal: ({ children }) => (
                <p
                    className="article-serif article-paragraph-spaced"
                    style={{
                        fontSize: 'clamp(1.0625rem, 2vw, 1.125rem)',
                        lineHeight: '1.85',
                        marginBottom: '1.5rem',
                        marginTop: '0',
                        color: '#2b3a33'
                    }}
                >
                    {children}
                </p>
            ),
            blockquote: ({ children }) => (
                <blockquote
                    className="article-blockquote"
                    style={{
                        borderLeftColor: categoryColor,
                        marginBottom: '2rem',
                        marginTop: '2rem'
                    }}
                >
                    {children}
                </blockquote>
            ),
        },
        list: {
            bullet: ({ children }) => (
                <ul
                    className={`article-list blog-list-${normalizeSlug(primaryCategory?.slug?.current) || 'default'}`}
                    style={{
                        marginBottom: '1.5rem',
                        marginTop: '1.25rem',
                        paddingLeft: '0.25rem',
                        listStyle: 'none'
                    }}
                >
                    {children}
                </ul>
            ),
            number: ({ children }) => (
                <ol
                    className={`article-list blog-list-${normalizeSlug(primaryCategory?.slug?.current) || 'default'}`}
                    style={{
                        marginBottom: '1.5rem',
                        marginTop: '1.25rem',
                        paddingLeft: '1.5rem'
                    }}
                >
                    {children}
                </ol>
            ),
        },
        listItem: {
            bullet: ({ children }) => (
                <li
                    className="article-serif"
                    style={{
                        marginBottom: '0.5rem',
                        paddingLeft: '1.25rem',
                        fontSize: 'clamp(1rem, 2vw, 1.0625rem)',
                        lineHeight: '1.75',
                        color: '#2b3a33',
                        position: 'relative'
                    }}
                >
                    <span style={{
                        position: 'absolute',
                        left: '0',
                        top: '0.55em',
                        width: '5px',
                        height: '5px',
                        backgroundColor: categoryColor,
                        borderRadius: '50%'
                    }} />
                    {children}
                </li>
            ),
            number: ({ children }) => (
                <li
                    className="article-serif"
                    style={{
                        marginBottom: '0.5rem',
                        paddingLeft: '0.375rem',
                        fontSize: 'clamp(1rem, 2vw, 1.0625rem)',
                        lineHeight: '1.75',
                        color: '#2b3a33'
                    }}
                >
                    {children}
                </li>
            ),
        },
    }
}
